import { useMemo } from 'react';

import { Icon } from './Icon';
import { Wheel, type WheelItem } from './Wheel';
import './WhenPicker.css';

/** Сколько дней вперёд можно запланировать встречу. */
const HORIZON_DAYS = 60;
/** Шаг минут в барабане. */
const MINUTE_STEP = 5;

const pad = (n: number) => String(n).padStart(2, '0');

export interface When {
  /** Дата в формате `2026-08-06`. */
  day: string;
  /** Часы 0–23. */
  hour: number;
  /** Минуты, кратные шагу. */
  minute: number;
}

/**
 * Выбор даты и времени тремя барабанами.
 *
 * Нативные input[type=date] и datetime-local выглядят по-разному в каждом
 * браузере, а системный календарь в Chrome открывается только через
 * showPicker(), которого нет в Safari. Собственные барабаны ведут себя
 * одинаково везде и не требуют попадать курсором в мелкий значок.
 */
export function WhenPicker({
  value,
  onChange,
  durationMinutes,
}: {
  value: When;
  onChange: (next: When) => void;
  durationMinutes: number;
}) {
  const days = useMemo(buildDays, []);
  const hours = useMemo(buildHours, []);
  const minutes = useMemo(buildMinutes, []);

  const start = toDate(value);
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  // Встреча, начатая поздно вечером, заканчивается уже завтра.
  const spillsOver = end.getDate() !== start.getDate();
  const inPast = start.getTime() < Date.now() - 60_000;

  return (
    <div className="when-picker">
      <div className="when-picker__wheels">
        <Wheel
          label="День"
          items={days}
          value={value.day}
          onChange={(day) => onChange({ ...value, day })}
        />

        <span className="when-picker__sep" aria-hidden />

        <Wheel
          label="Часы"
          items={hours}
          value={String(value.hour)}
          onChange={(hour) => onChange({ ...value, hour: Number(hour) })}
          itemHeight={40}
        />

        <span className="when-picker__colon" aria-hidden>
          :
        </span>

        <Wheel
          label="Минуты"
          items={minutes}
          value={String(value.minute)}
          onChange={(minute) => onChange({ ...value, minute: Number(minute) })}
          itemHeight={40}
        />
      </div>

      <p className={`when-picker__summary ${inPast ? 'when-picker__summary--warn' : ''}`}>
        <Icon name={inPast ? 'alert' : 'clock'} size={16} />
        {inPast ? (
          'Это время уже прошло — выберите позже'
        ) : (
          <>
            {describeDay(value.day)} с <b>{pad(value.hour)}:{pad(value.minute)}</b> до{' '}
            <b>
              {pad(end.getHours())}:{pad(end.getMinutes())}
            </b>
            {spillsOver && <span className="when-picker__next-day"> следующего дня</span>}
          </>
        )}
      </p>
    </div>
  );
}

// ── Наполнение барабанов ──────────────────────────────────────────────

function buildDays(): WheelItem[] {
  const items: WheelItem[] = [];

  for (let offset = 0; offset < HORIZON_DAYS; offset++) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + offset);

    const label =
      offset === 0
        ? 'Сегодня'
        : offset === 1
          ? 'Завтра'
          : date.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });

    items.push({
      value: isoDay(date),
      label,
      note: offset <= 1 ? date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) : undefined,
    });
  }

  return items;
}

const buildHours = (): WheelItem[] =>
  Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: pad(h) }));

const buildMinutes = (): WheelItem[] =>
  Array.from({ length: 60 / MINUTE_STEP }, (_, i) => ({
    value: String(i * MINUTE_STEP),
    label: pad(i * MINUTE_STEP),
  }));

// ── Преобразования ────────────────────────────────────────────────────

export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Ближайшее деление вверх от текущего момента плюс запас. */
export function defaultWhen(): When {
  const date = new Date(Date.now() + 15 * 60_000);
  date.setSeconds(0, 0);
  date.setMinutes(Math.ceil(date.getMinutes() / MINUTE_STEP) * MINUTE_STEP);

  return { day: isoDay(date), hour: date.getHours(), minute: date.getMinutes() };
}

export function toDate(when: When): Date {
  const [year, month, day] = when.day.split('-').map(Number);
  return new Date(year, month - 1, day, when.hour, when.minute, 0, 0);
}

export const toTimestamp = (when: When) => toDate(when).getTime();

function describeDay(iso: string): string {
  const today = isoDay(new Date());
  if (iso === today) return 'Сегодня';

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (iso === isoDay(tomorrow)) return 'Завтра';

  return toDate({ day: iso, hour: 0, minute: 0 }).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
  });
}
